// src/pages/B2B/event-reports/components/ReportBuilderForm.tsx
import React from 'react';
import type { Env } from '@/pages/Admin/shared';
import type { BuilderFormState } from '../types';
import { ReportImageUpload } from './ReportImageUpload';
import { TimezoneSelect } from './TimezoneSelect';
import { ReportTypeSelect } from './ReportTypeSelect';
import { SpotlightVenueSelect } from './SpotlightVenueSelect';

interface Props {
  env: Env;
  form: BuilderFormState;
  onChange: (next: BuilderFormState) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitting: boolean;
  error: string | null;
}

function defaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Phoenix';
  } catch {
    return 'America/Phoenix';
  }
}

export function ReportBuilderForm({
  env,
  form,
  onChange,
  onSubmit,
  onCancel,
  submitting,
  error,
}: Props): React.ReactElement {
  const update = <K extends keyof BuilderFormState>(key: K, value: BuilderFormState[K]) =>
    onChange({ ...form, [key]: value });

  const submitDisabled =
    submitting ||
    !form.event_name.trim() ||
    !form.from_local ||
    !form.to_local ||
    !form.timezone ||
    form.from_local >= form.to_local;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!submitDisabled) onSubmit();
      }}
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-2">
        <label className="text-xs uppercase tracking-wider text-gray-500">Event Name</label>
        <input
          type="text"
          value={form.event_name}
          onChange={(e) => update('event_name', e.target.value)}
          maxLength={120}
          placeholder="The Phoenix Crawl"
          className="px-3 py-2 text-sm bg-gray-900 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:border-violet-500"
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs uppercase tracking-wider text-gray-500">Report Type</label>
        <ReportTypeSelect
          value={form.report_type}
          onChange={(v) => update('report_type', v)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-wider text-gray-500">From</label>
          <input
            type="datetime-local"
            value={form.from_local}
            onChange={(e) => update('from_local', e.target.value)}
            className="px-3 py-2 text-sm bg-gray-900 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:border-violet-500"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-wider text-gray-500">To</label>
          <input
            type="datetime-local"
            value={form.to_local}
            onChange={(e) => update('to_local', e.target.value)}
            min={form.from_local || undefined}
            className="px-3 py-2 text-sm bg-gray-900 border border-gray-700 rounded-lg text-gray-200 focus:outline-none focus:border-violet-500"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs uppercase tracking-wider text-gray-500">Timezone</label>
        <TimezoneSelect
          value={form.timezone || defaultTimezone()}
          onChange={(tz) => update('timezone', tz)}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs uppercase tracking-wider text-gray-500">Spotlight Venue (optional)</label>
        <SpotlightVenueSelect
          env={env}
          timezone={form.timezone}
          mode={form.spotlight_mode}
          businessId={form.spotlight_business_id}
          onChange={(next) => onChange({ ...form, spotlight_mode: next.mode, spotlight_business_id: next.businessId })}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs uppercase tracking-wider text-gray-500">Location Label (optional)</label>
        <input
          type="text"
          value={form.location_label}
          onChange={(e) => update('location_label', e.target.value)}
          maxLength={80}
          placeholder="Phoenix, AZ"
          className="px-3 py-2 text-sm bg-gray-900 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:border-violet-500"
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs uppercase tracking-wider text-gray-500">Cover Image</label>
        <ReportImageUpload
          env={env}
          value={{ staging_key: form.image_staging_key, preview_url: form.image_preview_url }}
          onChange={(next) => onChange({ ...form, image_staging_key: next.staging_key, image_preview_url: next.preview_url })}
        />
      </div>

      {error && (
        <div className="rounded-lg border border-red-700/50 bg-red-950/40 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitDisabled}
          className="px-4 py-2 text-sm font-semibold text-white bg-violet-600 border border-violet-500 rounded-lg hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? 'Generating…' : 'Generate Report'}
        </button>
      </div>
    </form>
  );
}

export default ReportBuilderForm;
